import {DeleteOutlined, EditOutlined} from '@ant-design/icons';
import {Button, message, Modal, Space} from 'antd';
import {
  AuthorizedUserFieldsFragment,
  UpdateAuthorizedUserInput,
  useDeleteAuthorizedUserMutation,
  useUpdateAuthorizedUserMutation
} from 'api/generated';
import {authorizedUsersQuery} from 'api/queries';
import {Formik, FormikHelpers} from 'formik';
import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import {AuthorizedUserForm} from './AuthorizedUserForm';
import {StoresProps} from './AuthorizedUsers';

const {confirm} = Modal;

type Props = {
  user: AuthorizedUserFieldsFragment;
} & StoresProps;

export const UserActions: React.FC<Props> = ({stores, loadingStores, user}) => {
  const [isOpen, setOpen] = useState(false);
  const {accountId} = useParams<{accountId: string}>();
  const [deleteUser] = useDeleteAuthorizedUserMutation({
    updateQuery: {
      query: authorizedUsersQuery,
      variables: {accountId}
    }
  });
  const [updateUser] = useUpdateAuthorizedUserMutation();

  const handleSubmit = async (
    input: UpdateAuthorizedUserInput,
    {setSubmitting, resetForm}: FormikHelpers<UpdateAuthorizedUserInput>
  ) => {
    try {
      await updateUser({variables: {input: {...input, storeId: input.storeId || null}}});
      setOpen(false);
      resetForm();
    } finally {
      setSubmitting(false);
    }
  };
  const handleDelete = () => {
    confirm({
      title: 'Are you sure?',
      okText: 'Delete user',
      okType: 'danger',
      content: (
        <>
          This action will completely delete <b>{user.userEmail}</b> user.
        </>
      ),
      onOk: async () => {
        await deleteUser({
          variables: {
            userEmail: user.userEmail,
            accountId
          },
          mapResultToUpdate: () => user
        });
        message.success('User deleted successfully.');
      }
    });
  };

  return (
    <>
      <Space>
        <Button size="small" onClick={() => setOpen(true)}>
          <EditOutlined /> Edit
        </Button>
        <Button size="small" danger onClick={() => handleDelete()}>
          <DeleteOutlined />
        </Button>
      </Space>
      <Formik<UpdateAuthorizedUserInput>
        onSubmit={handleSubmit}
        enableReinitialize
        initialValues={{
          userEmail: user.userEmail,
          accountId,
          accessLevel: user.accessLevel,
          storeId: user.storeId
        }}>
        {({handleSubmit, isSubmitting}) => (
          <Modal
            open={isOpen}
            title="Update authorized user"
            okText="Update"
            confirmLoading={isSubmitting}
            onOk={() => handleSubmit()}
            onCancel={() => setOpen(false)}>
            <AuthorizedUserForm stores={stores} loadingStores={loadingStores} />
          </Modal>
        )}
      </Formik>
    </>
  );
};
